import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { isEmpty, isNil } from "ramda";

const COLORS = [
  "#5470C6",
  "#91CC75",
  "#EE6666",
  "#9A60B4",
  "#FAC858",
  "#EA7CCC",
  "#FC8452",
  "#73C0DE",
  "#3BA272",
  "#FF5733",
  "#33FF57",
  "#5733FF",
  "#FFD700",
  "#40E0D0",
  "#8A2BE2",
  "#FF69B4",
  "#7FFF00",
  "#DC143C",
  "#00CED1",
  "#4682B4",
  "#32CD32",
  "#FF4500",
  "#9400D3",
  "#20B2AA",
  "#FFDAB9",
  "#B22222",
  "#00FA9A",
  "#191970",
  "#ADFF2F",
];

// Márgenes del layout en % del ancho total
const LAYOUT = { left: 2, right: 2, gap: 3 };

const roundDown = (v) => (Number.isFinite(v) ? Math.floor(v * 100) / 100 : undefined);
const roundUp = (v) => (Number.isFinite(v) ? Math.ceil(v * 100) / 100 : undefined);

const getRange = (series) => {
  let min = Infinity;
  let max = -Infinity;
  series.forEach((s) =>
    s.data?.forEach((v) => {
      if (typeof v !== "number") return;
      if (v < min) min = v;
      if (v > max) max = v;
    }),
  );
  return [min, max];
};

// Una serie por sección, cada una apuntando a los ejes de su grid
const buildSeries = (items) => {
  let colorIndex = 0;
  return items.flatMap((item, gridIndex) =>
    (item.data.series ?? []).map((s) => ({
      ...s,
      type: "line",
      xAxisIndex: gridIndex,
      yAxisIndex: gridIndex,
      color: COLORS[colorIndex++ % COLORS.length],
    })),
  );
};

const withMarkLines = (series, start, end) =>
  series.map((s) => ({
    ...s,
    markLine: {
      symbol: "none",
      silent: true,
      data: [start, end].map((v) => ({
        yAxis: v,
        lineStyle: { color: "red", width: 2, type: "solid" },
        label: { show: false },
      })),
    },
  }));

const ReversedLineChart = ({
  prefix,
  dataList = [],
  sections = [],
  measureName,
  withToolbox = false,
  height = "100vh",
  sharedScale = false, // true: las 4 secciones comparten el mismo rango del eje X
  resetZoomHandler = () => {},
  pointsSelectHandler = () => {},
  startIndex,
  endIndex,
  isAlert,
  alertMeasure,
  afterAlert,
  assetModule,
  configData,
  alertLines,
  setAlertLines = () => {},
  setLoadingAverage = () => {},
  setLoadingAverageTemporal = () => {},
  info,
  intl,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const resetZoomRef = useRef(resetZoomHandler);

  useEffect(() => {
    resetZoomRef.current = resetZoomHandler;
  }, [resetZoomHandler]);

  const gridCountRef = useRef(0);
  const sharedScaleRef = useRef(sharedScale);

  useEffect(() => {
    sharedScaleRef.current = sharedScale;
  }, [sharedScale]);

  const getItems = () =>
    dataList.map((data, i) => ({ data, name: sections[i] ?? `${i + 1}` })).filter((item) => !isNil(item.data));

  // 1) Init único + resize
  useEffect(() => {
    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    chart.on("restore", () => resetZoomRef.current());
    chart.on("dataZoom", (params) => {
      const items = params.batch ?? [params];

      const getRange = (it) => {
        if (it.startValue != null && it.endValue != null) {
          return { startValue: it.startValue, endValue: it.endValue };
        }
        if (it.start != null && it.end != null) {
          return { start: it.start, end: it.end };
        }
        return null;
      };

      const syncAxis = (axisName) => {
        const source = items.find(
          (it) => typeof it.dataZoomId === "string" && it.dataZoomId.includes(axisName) && getRange(it),
        );
        if (!source) return [];

        const idPrefix = source.dataZoomId.replace(/\d+$/, "");
        const range = getRange(source);
        const batch = [];
        for (let i = 0; i < gridCountRef.current; i++) {
          const id = `${idPrefix}${i}`;
          if (id !== source.dataZoomId) batch.push({ dataZoomId: id, ...range });
        }
        return batch;
      };

      const batch = [...syncAxis("yAxis"), ...(sharedScaleRef.current ? syncAxis("xAxis") : [])];

      if (batch.length > 0) {
        chart.dispatchAction({ type: "dataZoom", batch }, { silent: true });
      }
    });
    chart.on("mousewheel", (params) => params.event?.preventDefault?.());

    let rafId = null;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => chart.resize());
    });
    resizeObserver.observe(chartRef.current);

    let lastRatio = window.devicePixelRatio;
    const interval = setInterval(() => {
      if (window.devicePixelRatio !== lastRatio) {
        lastRatio = window.devicePixelRatio;
        chart.resize();
      }
    }, 300);

    return () => {
      clearInterval(interval);
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      chart.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  // 2) Opciones: N grids, una leyenda
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    const items = getItems();
    if (isEmpty(items)) {
      chart.clear();
      return;
    }

    const n = items.length;
    gridCountRef.current = n;
    const width = (100 - LAYOUT.left - LAYOUT.right - LAYOUT.gap * (n - 1)) / n;
    const leftOf = (i) => LAYOUT.left + i * (width + LAYOUT.gap);

    const moduleData = configData.modules.find((m) => m.name === assetModule);
    const unit = moduleData?.packages.find((p) => p.name === measureName)?.unit ?? "";

    const ranges = items.map((item) => getRange(item.data.series ?? []));
    const globalMin = Math.min(...ranges.map((r) => r[0]));
    const globalMax = Math.max(...ranges.map((r) => r[1]));

    let series = buildSeries(items);
    const hasAlertLines = !isNil(alertLines) && alertLines.start !== null && alertLines.end !== null;
    if (hasAlertLines) series = withMarkLines(series, alertLines.start, alertLines.end);

    const options = {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: { link: [{ yAxisIndex: "all" }] }, // el puntero se sincroniza entre secciones
        position: (pt) => [pt?.[0], "10%"],
        valueFormatter: (value) => (typeof value === "number" ? value.toFixed(2) : value),
      },
      legend: { data: series.map((s) => s.name), top: 0, type: "scroll" },
      title: items.map((item, i) => ({
        text: item.name,
        left: `${leftOf(i) + width / 2}%`,
        top: 30,
        textAlign: "center",
        textStyle: { fontSize: 13 },
      })),
      grid: items.map((_, i) => ({
        left: `${leftOf(i)}%`,
        width: `${width}%`,
        top: 70,
        bottom: 45,
        containLabel: true,
      })),
      xAxis: items.map((_, i) => {
        const [min, max] = sharedScale ? [globalMin, globalMax] : ranges[i];
        return {
          type: "value",
          gridIndex: i,
          min: roundDown(min),
          max: roundUp(max),
          name: `${intl.formatMessage({ id: measureName })} (${unit})`,
          nameLocation: "middle",
          nameGap: 28,
        };
      }),
      yAxis: items.map((item, i) => ({
        type: "category",
        gridIndex: i,
        // inverse: true, // descomenta si quieres profundidad 0 arriba
        data: (item.data.yAxis?.data ?? []).map((num) => (typeof num === "number" ? parseFloat(num.toFixed(1)) : num)),
        boundaryGap: false,
        splitLine: { show: false },
        axisLine: { onZero: false },
        name: i === 0 ? `${intl.formatMessage({ id: "depth" })} (m)` : "",
        nameLocation: "middle",
        nameGap: 35,
      })),
      toolbox: withToolbox
        ? {
            feature: {
              dataZoom: {
                yAxisIndex: "all",
                xAxisIndex: "all",
                brushStyle: {
                  color: "#5470C655",
                  borderType: "solid",
                  borderWidth: 1,
                  borderColor: "#5470C6",
                  shadowColor: "#5470C6",
                },
                title: {
                  zoom: intl.formatMessage({ id: "Zoom in" }),
                  back: intl.formatMessage({ id: "Zoom out" }),
                },
              },
              restore: { title: intl.formatMessage({ id: "Restore zoom" }) },
              saveAsImage: { title: intl.formatMessage({ id: "Save as image" }) },
              myCustomCSVDownload: {
                show: true,
                title: intl.formatMessage({ id: "Download CSV" }),
                icon: "image://csv-icon80.png",
                onclick: () => downloadCSV(),
              },
            },
          }
        : {},
      series,
    };

    // notMerge: true porque si cambia el nº de secciones no deben quedar grids/ejes antiguos
    chart.setOption(options, { notMerge: true, lazyUpdate: true, silent: true });

    return () => {
      setLoadingAverage(false);
      setLoadingAverageTemporal(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList, sections, prefix, sharedScale]);

  // 3) Zoom/markLines cuando llega una alerta
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart || isNil(startIndex) || isNil(endIndex) || !isAlert) return;

    if (measureName === "strain" && alertMeasure === "leak") {
      if (!afterAlert) pointsSelectHandler(startIndex, endIndex, false);
      return;
    }

    const items = getItems();
    if (isEmpty(items)) return;
    const total = items[0].data.yAxis?.data?.length ?? 0;

    let i = startIndex;
    let j = endIndex;
    if (j - i >= 64) {
      let centerPoint = parseInt((j - i) / 2) + i;
      i = centerPoint >= 32 ? centerPoint - 32 : 0;
      centerPoint += 32;
      j = centerPoint < total ? centerPoint : total - 1;
    }
    setAlertLines({ start: i, end: j });

    const iniZoom = Math.floor((startIndex * 100) / total) - 1;
    const finZoom = Math.floor((endIndex * 100) / total) + 2;

    chart.setOption(
      {
        series: withMarkLines(buildSeries(items), i, j),
        dataZoom: [{ type: "inside", yAxisIndex: "all", start: iniZoom, end: finZoom }],
      },
      { notMerge: false, lazyUpdate: true, silent: true },
    );

    if (!afterAlert && !isNil(alertMeasure)) pointsSelectHandler(startIndex, endIndex, false);
    if (isNil(alertMeasure) && !afterAlert) pointsSelectHandler(startIndex, endIndex, afterAlert);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);

  function downloadCSV() {
    const items = getItems();
    const header = [
      info.asset,
      info.equipment.name,
      info.equipment.channels[info.channel - 1].name,
      info.dateTime,
    ].join(",");

    const columns = [];
    items.forEach((item) => {
      columns.push({ title: `${item.name}_depth`, values: item.data.yAxis?.data ?? [] });
      (item.data.series ?? []).forEach((s) => columns.push({ title: `${item.name}_${s.name}`, values: s.data ?? [] }));
    });

    const rows = Math.max(...columns.map((c) => c.values.length));
    const lines = [];
    for (let r = 0; r < rows; r++) {
      lines.push(columns.map((c) => (typeof c.values[r] === "number" ? c.values[r].toFixed(2) : "")).join(","));
    }

    const csvData = [header, columns.map((c) => c.title).join(","), ...lines].join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${info.dateTime}_${measureName}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div
      id={`${prefix}-stacked-line-chart`}
      ref={chartRef}
      style={{ width: "100%", height, minWidth: 0, position: "relative", overflow: "hidden" }}
    />
  );
};

export default ReversedLineChart;
